import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { supabase } from '../../../lib/supabase';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Admin V2 Promo — Campagnes promo + codes. Feature : pages.promo.admin.
 * Route : /admin/promo + /admin/v2/promo. Données manuelles.
 */
const AdminV2Promo = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName, tenantId, agencyId } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [codesByCampaign, setCodesByCampaign] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', status: 'draft' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      let cq = supabase.from('promo_campaigns')
        .select('id, name, description, status, starts_at, ends_at, created_at')
        .eq('tenant_id', tenantId).order('created_at', { ascending: false });
      if (agencyId) cq = cq.eq('agency_id', agencyId);
      const { data: camps, error: e1 } = await cq.limit(1000);
      if (e1) throw e1;
      setCampaigns(camps || []);

      const ids = (camps || []).map((c) => c.id);
      if (ids.length > 0) {
        const { data: codes } = await supabase.from('promo_codes')
          .select('id, campaign_id, code, max_uses, used_count')
          .in('campaign_id', ids);
        const grouped = {};
        for (const c of codes || []) (grouped[c.campaign_id] = grouped[c.campaign_id] || []).push(c);
        setCodesByCampaign(grouped);
      } else {
        setCodesByCampaign({});
      }
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [tenantId, agencyId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !agencyId) return;
    setSaving(true);
    try {
      const { error: e1 } = await supabase.from('promo_campaigns').insert({
        tenant_id: tenantId, agency_id: agencyId,
        name: form.name, description: form.description, status: form.status,
      });
      if (e1) throw e1;
      setShowCreate(false); setForm({ name: '', description: '', status: 'draft' });
      await load();
    } catch (err) { alert(err.message || String(err)); } finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">{t('promoV2.title', 'Promo campaigns')}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName} · {campaigns.length}</p>
            </div>
            <button type="button" onClick={() => setShowCreate((s) => !s)}
              className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">{t('promoV2.create', 'New campaign')}</button>
          </div>
        </header>
        <div className="p-4 md:p-6 space-y-4">
          {showCreate && (
            <form onSubmit={submit} className="rounded-2xl bg-card border border-border p-5 space-y-3">
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required
                placeholder={t('promoV2.formName', 'Campaign name')}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary" />
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2}
                placeholder={t('promoV2.formDesc', 'Description')}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary" />
              <div className="flex items-center gap-3 flex-wrap">
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="px-3 py-2 rounded-lg bg-background border border-border text-base">
                  <option value="draft">{t('promoV2.status.draft', 'Draft')}</option>
                  <option value="active">{t('promoV2.status.active', 'Active')}</option>
                  <option value="ended">{t('promoV2.status.ended', 'Ended')}</option>
                </select>
                <button type="submit" disabled={saving} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60">{saving ? '…' : t('promoV2.save', 'Save')}</button>
                <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-2 rounded-lg border border-border text-sm">{t('common.cancel', 'Cancel')}</button>
              </div>
            </form>
          )}

          {error && <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error">{String(error.message || error)}</div>}

          {loading ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : campaigns.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">{t('promoV2.empty', 'No campaigns yet.')}</div>
          ) : (
            <div className="space-y-3">
              {campaigns.map((c) => (
                <section key={c.id} className="rounded-2xl bg-card border border-border p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="font-heading font-semibold text-foreground truncate">{c.name}</h2>
                      {c.description && <p className="text-sm text-muted-foreground mt-1">{c.description}</p>}
                    </div>
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${
                      c.status === 'active' ? 'bg-success/10 text-success' : c.status === 'ended' ? 'bg-muted text-muted-foreground' : 'bg-warning/10 text-warning'
                    }`}>{t(`promoV2.status.${c.status}`, c.status)}</span>
                  </div>
                  {(codesByCampaign[c.id] || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {codesByCampaign[c.id].map((code) => (
                        <span key={code.id} className="text-xs font-data px-2 py-1 rounded-lg bg-muted text-foreground">
                          {code.code} {code.max_uses != null ? `(${code.used_count}/${code.max_uses})` : `(${code.used_count})`}
                        </span>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2Promo;
